import React, { useState } from "react";
import axios from "axios";
import {
    TextField,
    Paper,
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Alert,
    Rating,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    Stack,
} from "@mui/material"

import { NumericFormat, PatternFormat } from "react-number-format";

// TODO: replace with the real Google Business review link, e.g.
// "https://g.page/r/XXXXXXXXXXXX/review" or a
// "https://search.google.com/local/writereview?placeid=..." URL
const GOOGLE_REVIEW_URL = "https://g.page/r/REPLACE_ME/review";

// Referral incentive shown alongside the "recommend us" question.
const REFERRAL_AMOUNT = "$25";

const NumberFormatPhone = (props) => {
    const { inputRef, onChange, ...other } = props;
    return (
      <PatternFormat
        {...other}
        getInputRef={inputRef}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        format="(###) ###-####"
        mask="_"
      />
    );
  };

const RATING_QUESTIONS = [
  { key: "overallStay", label: "How was your stay?" },
  { key: "roomClean", label: "Was the room clean?" },
  { key: "happyWithStay", label: "Are you happy with the stay?" },
  { key: "managementTreatment", label: "Are you happy with how you were treated by management?" },
];

const CustomerInquiry = (props) => {
    const [name, setName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState(null);

    // Optional stay context — guest tells us, nothing is required.
    const [roomNumber, setRoomNumber] = useState("");
    const [stayFrom, setStayFrom] = useState("");
    const [stayTo, setStayTo] = useState("");

    // Ratings — one entry per RATING_QUESTIONS key, 0 = not answered.
    const [ratings, setRatings] = useState({
      overallStay: 0,
      roomClean: 0,
      happyWithStay: 0,
      managementTreatment: 0,
    });

    const [recommend, setRecommend] = useState(""); // "yes" | "no" | ""
    const [feedback, setFeedback] = useState("");

    const [successfullySaved, setSuccessfullySaved] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const url = process.env.REACT_APP_SERVER_URL;

    const handleRatingChange = (key, value) => {
      setRatings((prev) => ({ ...prev, [key]: value || 0 }));
    };

    // Only nudge for a Google review when every rating question was
    // answered and every answer is 4 or 5 stars.
    const allRatingsPositive = RATING_QUESTIONS.every(
      (q) => ratings[q.key] >= 4
    );

    const saveCustomerInquiry = async () => {
        setSubmitting(true);
        setErrorMsg("");
        try {
            const response = await axios.post(`${url}/customerInquiry`, {
                name,
                phoneNumber,
                roomNumber: roomNumber || null,
                stayFrom: stayFrom || null,
                stayTo: stayTo || null,
                ratings,
                recommend: recommend || null,
                feedback,
            });
            if (response) {
              setSuccessfullySaved(true);
            }
        }
        catch (error) {
            console.log(error);
            setErrorMsg("Something went wrong submitting your feedback. Please try again.");
        }
        finally {
          setSubmitting(false);
        }
    }

    return (
      <>
      <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" style={{ backgroundColor: "black" }}>
        <Toolbar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            Tidy Inn
          </Typography>
        </Toolbar>
      </AppBar>
    </Box>
    <br></br>

      <Paper elevation={3} style={{
        marginLeft: "10%", marginRight: "10%", marginTop: "2%",
        padding: "2%"
      }}>
        <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
           Feedback Form
          </Typography><br></br>

          {successfullySaved ? (
            <>
              <Alert severity="success">
                We appreciate and value your feedback. We will get back to you shortly at the number you have provided.
                <br></br>
                Regards<br></br>
                Management
              </Alert>
              <br></br>

              {allRatingsPositive && (
                <Paper variant="outlined" sx={{ p: 3, textAlign: "center", bgcolor: "#fffdf5" }}>
                  <Typography variant="h6" gutterBottom>
                    We're so glad you had a great stay!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Would you mind sharing a 5 star review on our Google profile? It really helps us out.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Leave us a Google review
                  </Button>
                </Paper>
              )}
            </>
          ) : (
            <>
              {errorMsg && (
                <>
                  <Alert severity="error">{errorMsg}</Alert>
                  <br></br>
                </>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField id="outlined-basic" label="Full Name" variant="outlined" fullWidth
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                  }} />

                <TextField
                  id="outlined-basic"
                  label="Phone Number"
                  variant="outlined"
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value)
                  }}
                  InputProps={{
                    inputComponent: NumberFormatPhone,
                  }}
                />
              </Stack>

              <br></br>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Which room did you stay in, and for how long? (optional)
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Room Number"
                  variant="outlined"
                  fullWidth
                  value={roomNumber}
                  onChange={(e) => setRoomNumber(e.target.value)}
                />
                <TextField
                  label="Check-in date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={stayFrom}
                  onChange={(e) => setStayFrom(e.target.value)}
                />
                <TextField
                  label="Check-out date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={stayTo}
                  onChange={(e) => setStayTo(e.target.value)}
                />
              </Stack>

              <br></br>
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Tell us about your stay
              </Typography>

              <Stack spacing={2} sx={{ mb: 2 }}>
                {RATING_QUESTIONS.map((q) => (
                  <Box
                    key={q.key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "440px 1fr" },
                      alignItems: "center",
                      rowGap: 1,
                      columnGap: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Typography variant="body">
                      {q.label}
                    </Typography>
                    <Rating
                      size="large"
                      value={ratings[q.key]}
                      onChange={(e, newValue) => handleRatingChange(q.key, newValue)}
                    />
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <FormLabel component="legend" sx={{ mb: 1 }}>
                Will you recommend Tidy Inn to your friends?
              </FormLabel>
              <Typography variant="body" color="text.secondary" sx={{ mb: 1 }}>
                We offer a {REFERRAL_AMOUNT} referral gift when you refer a new weekly or monthly guest.
              </Typography>
              <RadioGroup
                row
                value={recommend}
                onChange={(e) => setRecommend(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>

              <br></br>
              <Divider sx={{ my: 2 }} />

              <TextField id="outlined-basic" label="Any improvements we can do?" variant="outlined" fullWidth multiline
                rows={4} value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value)
                }} />

              <br></br><br></br>
              <Button
                variant="contained"
                color="success"
                disabled={submitting || !name || !phoneNumber}
                onClick={saveCustomerInquiry}
              >
                {submitting ? "Saving..." : "Save and Submit"}
              </Button>
            </>
          )}

      </Paper>
      </>
    );
}
export default CustomerInquiry;